import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * VoiceInput — Click to start/stop recording with live transcription indicator
 * Click once to start recording, click again to stop and transcribe
 */
const VoiceInput = ({ onTranscript, className = '', size = 'md', showHint = true }) => {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const sizes = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };
  const iconSizes = { sm: 'w-3.5 h-3.5', md: 'w-4 h-4', lg: 'w-5 h-5' };

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      setSeconds(0);

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 1000) {
          toast.error('Recording too short — try speaking longer');
          setRecording(false);
          return;
        }

        setProcessing(true);
        setRecording(false);
        try {
          const formData = new FormData();
          formData.append('file', blob, 'voice.webm');
          const token = localStorage.getItem('token');
          const res = await fetch(API + '/api/voice/transcribe', {
            method: 'POST',
            headers: { Authorization: 'Bearer ' + token },
            body: formData,
          });
          if (res.ok) {
            const data = await res.json();
            if (data.text && data.text.trim()) {
              onTranscript(data.text.trim());
            } else {
              toast.error('Could not understand — try again');
            }
          } else {
            toast.error('Transcription failed — try again');
          }
        } catch (err) {
          toast.error('Connection error — try again');
        }
        setProcessing(false);
      };

      mediaRecorder.start(250); // Collect data every 250ms for responsiveness
      setRecording(true);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setSeconds(s => s + 1);
      }, 1000);
      
      toast.success('Recording — tap again when done');
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Microphone access denied — check your browser settings');
      } else {
        toast.error('Could not access microphone');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  const toggleRecording = () => {
    if (recording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  if (processing) {
    return (
      <div className={'flex items-center gap-2 ' + className}>
        <div className={sizes[size] + ' rounded-full bg-violet-100 flex items-center justify-center'}>
          <Loader2 className={iconSizes[size] + ' text-violet-600 animate-spin'} />
        </div>
        <span className="text-xs text-violet-600 font-medium animate-pulse">Transcribing...</span>
      </div>
    );
  }

  return (
    <div className={'flex items-center gap-2 ' + className}>
      <button
        type="button"
        onClick={toggleRecording}
        className={sizes[size] + ' rounded-full flex items-center justify-center transition-all ' + 
          (recording 
            ? 'bg-red-500 text-white animate-pulse scale-110 ring-4 ring-red-200' 
            : 'bg-gray-100 text-gray-500 hover:bg-orange-100 hover:text-orange-600'
          )}
        title={recording ? 'Tap to stop recording' : 'Tap to speak'}
        data-testid="voice-input-btn"
      >
        {recording ? <MicOff className={iconSizes[size]} /> : <Mic className={iconSizes[size]} />}
      </button>
      {recording && (
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-xs text-red-600 font-bold">{seconds}s</span>
          <span className="text-[10px] text-red-400">recording...</span>
        </div>
      )}
      {!recording && !processing && showHint && (
        <span className="text-[10px] text-gray-400 hidden sm:inline">Tap mic to speak</span>
      )}
    </div>
  );
};

export default VoiceInput;
